// Pannello "Documento intervento": descrizione dell'intervento + area disegnata
// → PDF con zone, norme di zona, estratti NTA e norme regionali pertinenti.
// Account OmniSurv con abbonamento OmniSurv o SurvMaps.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Field, Panel } from "./ui";
import { useToast } from "./Toast";
import SubscribeButton from "./SubscribeButton";
import { OMNI_LOGIN_URL } from "../lib/config";
import { docsApi, subscriptionApi } from "../lib/docs";

const DESCR_MIN = 20;
const DESCR_MAX = 2000;
const POLL_MS = 4000;

function formatDate(iso) {
  try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); } catch { return ""; }
}

// Senza un piano attivo: accesso su OmniSurv oppure abbonamento SurvMaps.
function Accesso({ auth }) {
  return (
    <>
      <p className="sm-panel-text">
        Descrivi l'intervento e disegna l'area: ricevi un PDF con le zone del PRG, le norme di zona,
        gli estratti delle NTA e le norme regionali che lo riguardano.
      </p>
      {auth.status === "free" ? (
        <>
          <p className="sm-panel-text">
            Il servizio è incluso nell'abbonamento SurvMaps e nell'abbonamento OmniSurv completo.
            {auth.email && <> Account: <b>{auth.email}</b>.</>}
          </p>
          <SubscribeButton auth={auth} block />
          <a className="sm-btn sm-btn--ghost sm-btn--sm" href={OMNI_LOGIN_URL} target="_blank" rel="noopener noreferrer">
            Oppure l'abbonamento OmniSurv completo
          </a>
        </>
      ) : (
        <>
          <p className="sm-panel-text">
            Serve un account OmniSurv con abbonamento SurvMaps o OmniSurv. Accedi (o registrati) in un'altra
            scheda e torna qui: SurvMaps riconosce l'accesso da solo.
          </p>
          <a className="sm-btn sm-btn--primary" href={OMNI_LOGIN_URL} target="_blank" rel="noopener noreferrer">
            Accedi o registrati su OmniSurv
          </a>
        </>
      )}
    </>
  );
}

export default function DocPanel({ city, shape, auth, onDraw, onClose }) {
  const toast = useToast();
  const token = auth.status === "paid" ? auth.token : null;
  const [me, setMe] = useState(null);
  const [descrizione, setDescrizione] = useState("");
  const [lavoro, setLavoro] = useState(null);    // { id } del documento in preparazione
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState([]);
  const pollRef = useRef(null);
  const refreshAuth = auth.refresh;

  const authError = useCallback((e) => {
    if (e.status === 401 || e.status === 402) {
      toast.error(e.status === 401 ? "Sessione OmniSurv scaduta: accedi di nuovo." : "Abbonamento non attivo.");
      refreshAuth();
      return true;
    }
    return false;
  }, [refreshAuth, toast]);

  const ricarica = useCallback(async () => {
    if (!token) return;
    try {
      const [m, l] = await Promise.all([docsApi.me(token), docsApi.list(token)]);
      setMe(m);
      setDocs(l.documenti.filter((d) => d.city_key === city.key).slice(0, 5));
      const inCorso = l.documenti.find((d) => d.stato === "in_corso");
      if (inCorso) setLavoro((w) => w || { id: inCorso.id });
    } catch (e) {
      if (!authError(e)) toast.error("Servizio documenti non raggiungibile.");
    }
  }, [token, city.key, authError, toast]);

  useEffect(() => { ricarica(); }, [ricarica]);

  const scarica = useCallback(async (id) => {
    try { await docsApi.download(token, id); } catch (e) { if (!authError(e)) toast.error(e.message); }
  }, [token, authError, toast]);

  // Attesa del documento: lo stato si interroga ogni POLL_MS finché non è pronto.
  useEffect(() => {
    if (!lavoro || !token) return undefined;
    let stop = false;
    const giro = async () => {
      try {
        const s = await docsApi.status(token, lavoro.id);
        if (stop) return;
        if (s.stato === "pronto") {
          setLavoro(null);
          toast.ok("Documento pronto.");
          await scarica(s.id);
          ricarica();
          return;
        }
        if (s.stato === "errore") {
          setLavoro(null);
          toast.error(`Documento non riuscito: ${s.errore || "errore interno"}`);
          ricarica();
          return;
        }
      } catch (e) {
        if (authError(e)) { setLavoro(null); return; }
      }
      if (!stop) pollRef.current = setTimeout(giro, POLL_MS);
    };
    pollRef.current = setTimeout(giro, POLL_MS);
    return () => { stop = true; clearTimeout(pollRef.current); };
  }, [lavoro, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const genera = async () => {
    if (!shape) { toast.info("Disegna prima l'area dell'intervento."); return; }
    const d = descrizione.trim();
    if (d.length < DESCR_MIN) { toast.info(`Descrivi l'intervento (almeno ${DESCR_MIN} caratteri).`); return; }
    setBusy(true);
    try {
      const r = await docsApi.generate(token, { city: city.key, geometry: shape.geometry, descrizione: d });
      setLavoro({ id: r.id });
      setMe((m) => (m ? { ...m, quota: r.quota } : m));
    } catch (e) {
      if (!authError(e)) toast.error(e.message);
    } finally { setBusy(false); }
  };

  const portale = async () => {
    try { window.location.href = (await subscriptionApi.portal(token)).url; } catch (e) { toast.error(e.message); }
  };

  const quota = me?.quota;
  const esaurita = quota && quota.usati >= quota.limite;

  return (
    <Panel title="Documento intervento" onClose={onClose} wide>
      {!token ? (
        <Accesso auth={auth} />
      ) : (
        <>
          <Field label={`Descrizione dell'intervento · ${descrizione.length}/${DESCR_MAX}`} htmlFor="docs-descr">
            <textarea id="docs-descr" className="sm-input" rows={6} maxLength={DESCR_MAX}
              placeholder="Es. realizzazione di un soppalco in legno di 25 mq al piano terra di un edificio residenziale, altezza utile 2,40 m"
              value={descrizione} onChange={(e) => setDescrizione(e.target.value)} disabled={!!lavoro}
              style={{ resize: "vertical", fontFamily: "var(--font-ui)" }} />
          </Field>

          {shape ? (
            <p className="sm-panel-text">Area: quella disegnata sulla mappa.</p>
          ) : (
            <>
              <p className="sm-panel-text">Disegna sulla mappa l'area dell'intervento:</p>
              <div className="sm-field-row">
                <Button variant="secondary" size="sm" icon="polygon" onClick={() => onDraw("polygon")}>Poligono</Button>
                <Button variant="secondary" size="sm" icon="square" onClick={() => onDraw("rectangle")}>Rettangolo</Button>
              </div>
            </>
          )}

          {lavoro ? (
            <p className="sm-panel-text">
              <span className="sm-spinner" style={{ display: "inline-block", verticalAlign: "middle", marginRight: 8 }} />
              Preparazione del documento: di solito 1–2 minuti. Puoi continuare a usare la mappa.
            </p>
          ) : (
            <Button block icon="file-text" busy={busy} disabled={esaurita} onClick={genera}>Genera documento</Button>
          )}

          {quota && (
            <p className="sm-panel-text sm-muted">
              {quota.usati} di {quota.limite} documenti usati {me.piano === "survmaps" ? "nel periodo di abbonamento" : "questo mese"}.
              {esaurita && " Quota esaurita."}
            </p>
          )}

          {docs.length > 0 && (
            <>
              <h3 className="sm-section-title">Ultimi documenti</h3>
              <ul className="sm-zone-list">
                {docs.map((d) => (
                  <li key={d.id} style={{ alignItems: "flex-start" }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="sm-mono sm-muted">{formatDate(d.created_at)}</span><br />
                      {d.descrizione}
                    </span>
                    {d.stato === "pronto" && <Button variant="secondary" size="sm" icon="download" onClick={() => scarica(d.id)}>PDF</Button>}
                    {d.stato === "errore" && <span className="sm-panel-error">non riuscito</span>}
                    {d.stato === "in_corso" && <span className="sm-spinner" />}
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="sm-list-actions">
            {me?.piano === "survmaps" && auth.plans?.survmaps && !auth.plans?.omnisurv && (
              <button type="button" className="sm-link" onClick={portale}>Gestisci abbonamento SurvMaps</button>
            )}
            {me?.piano === "omnisurv" && <span className="sm-muted">Incluso nell'abbonamento OmniSurv</span>}
          </div>
        </>
      )}
    </Panel>
  );
}
